import { isProduction, localDev, deploymentTier, fullAppSuiteRootURL, apiRoot } from '../../data/constants.ts';
import defaultAppInfo from '../../data/defaultAppInfo.cfg.json';
import { devlog } from '../../logic/utils.ts';
import { fetchResource, apiFetch, type UserData } from '../../logic/asApi.ts';

devlog(`${fullAppSuiteRootURL}/data/appSuite.cfg.json`);

// ============================================================================
// Interfaces
// ============================================================================

export interface HubSpotConfig {
  region: string;
  portalId: string;
  embedId: string;
  elementId: string;
}

export interface ParamateEndpoint {
  host: string;
  port?: number;
}

export interface AppSuiteConfig {
  apps: Record<string, any>;
  filterCategories: Array<{ tag: string; label: string; dictKey: string; devOnly?: boolean }>;
}

export interface ParamateDetails {
  APIHub?: string;
  APIKey: string;
  configuratorId: string;
}

export interface AppConfig {
  hubspot?: Record<string, HubSpotConfig>;
  paramate?: {
    onPrem?: ParamateEndpoint;
    external?: ParamateEndpoint;
  };
  configuratorDetails?: {
    production: ParamateDetails;
    staging: ParamateDetails;
    dev?: ParamateDetails;
    local?: ParamateDetails;
  };
}

export interface AppSettings extends AppConfig {
  userData?: UserData;
}

// ============================================================================
// App suite config
// ============================================================================

/** Per-deployment-tier mesh-transfer flag. */
export interface AcceptsMeshTransfer {
  dev: boolean;
  staging: boolean;
  production: boolean;
}

/** Raw acceptsMeshTransfer as it may arrive from the API or config JSON:
 * nested object (possibly partial, values boolean or 0/1), a JSON string of
 * that object (DB column), or a bare boolean applying to all tiers. */
type RawAcceptsMeshTransfer =
  | Partial<Record<'dev' | 'staging' | 'production', boolean | number>>
  | string
  | boolean
  | number
  | null;

interface AppListEntry {
  shortName: string;
  appNameDefaultText: string;
  appNameDictKey: string;
  descriptionDictKey: string;
  descriptionDefaultText: string | null;
  theme: string;
  categories: string;
  folderName: string | null;
  cardImage?: string | null;
  detailsPageURL: string;
  isFree: number | null;
  downloadEnabled?: boolean | number | null;
  acceptsFileTransfer: number | null;
  acceptsMeshTransfer?: RawAcceptsMeshTransfer;
  allowSelfTransfer: number | null;
  stickerTag: string | null;
  orderPrio: number;
  visibility?: number | null;
  visibility_staging?: number | null;
  visibility_production?: number | null;
  launchButtonBehaviour?: string;
  launchButtonBehaviour_staging?: string;
  launchButtonBehaviour_production?: string;
  devOnly: number | null;
  hide: number | null;
  live: number | null;
}

function toBool(val: boolean | number | null | undefined): boolean {
  return val != null && val !== 0 && val !== false;
}

/**
 * Normalize a raw acceptsMeshTransfer value to a partial per-tier object.
 * Accepts the nested object, a JSON string of it (DB column), or a bare
 * boolean (applies to every tier, warned about — the nested form is the
 * intended shape). Returns null when absent or unparseable.
 */
const meshTransferShapeWarned = new Set<string>();
function warnMeshTransferShape(appId: string, message: string, ...detail: unknown[]): void {
  if (meshTransferShapeWarned.has(appId)) return;
  meshTransferShapeWarned.add(appId);
  console.warn(`[AS-APP] acceptsMeshTransfer for "${appId}" ${message}`, ...detail);
}

function normalizeAcceptsMeshTransfer(raw: RawAcceptsMeshTransfer | undefined, appId: string):
  Partial<Record<'dev' | 'staging' | 'production', boolean | number>> | null {
  if (raw == null) return null;
  if (typeof raw === 'boolean' || typeof raw === 'number') {
    const val = toBool(raw);
    warnMeshTransferShape(appId, `is a bare ${typeof raw} (${raw}) — applying to all tiers; use {dev, staging, production} to control tiers individually.`);
    return { dev: val, staging: val, production: val };
  }
  if (typeof raw === 'string') {
    try {
      return normalizeAcceptsMeshTransfer(JSON.parse(raw), appId);
    } catch {
      warnMeshTransferShape(appId, 'is an unparseable string — ignoring:', raw);
      return null;
    }
  }
  if (typeof raw === 'object') return raw;
  warnMeshTransferShape(appId, 'has unexpected type — ignoring:', raw);
  return null;
}

function transformAppList(
  entries: AppListEntry[],
  jsonApps: Record<string, any> = {}
): Record<string, any> {
  const result: Record<string, any> = {};
  for (const e of entries) {
    const vStaging = e.visibility_staging ?? e.visibility;
    const vProd = e.visibility_production ?? e.visibility;
    const bStaging = e.launchButtonBehaviour_staging ?? e.launchButtonBehaviour ?? 'active';
    const bProd = e.launchButtonBehaviour_production ?? e.launchButtonBehaviour ?? 'active';

    // Per-tier mesh-transfer flag: API value wins, then the config-JSON
    // fallback, then the legacy acceptsFileTransfer boolean fills every tier.
    const legacyTransfer = e.acceptsFileTransfer != null
      ? toBool(e.acceptsFileTransfer)
      : toBool(jsonApps[e.shortName]?.acceptsFileTransfer);
    const rawMeshTransfer =
      normalizeAcceptsMeshTransfer(e.acceptsMeshTransfer, e.shortName)
      ?? normalizeAcceptsMeshTransfer(jsonApps[e.shortName]?.acceptsMeshTransfer, e.shortName);
    const meshTransfer: AcceptsMeshTransfer = {
      dev:        toBool(rawMeshTransfer?.dev        ?? legacyTransfer),
      staging:    toBool(rawMeshTransfer?.staging    ?? legacyTransfer),
      production: toBool(rawMeshTransfer?.production ?? legacyTransfer),
    };

    result[e.shortName] = {
      id: e.shortName,
      appNameDefaultText: e.appNameDefaultText,
      appNameDictKey: e.appNameDictKey,
      descriptionDictKey: e.descriptionDictKey,
      ...(e.descriptionDefaultText != null && { descriptionDefaultText: e.descriptionDefaultText }),
      theme: e.theme,
      categories: e.categories ? e.categories.split(',').map(s => s.trim()) : [],
      // Bare deployment folder name (`apps/` prefix stripped). The storefront
      // redirect path is `/apps/{folderName}`, so this is what we hand back as
      // `redirApp` — the app id can drift out of sync with the folder name.
      folderName: e.folderName ? e.folderName.replace(/^apps\//, '') : e.shortName,
      appUrl: e.folderName ? `apps/${e.folderName.replace(/^apps\//, '')}` : '',
      cardImage: (() => {
        const img = e.cardImage ?? jsonApps[e.shortName]?.cardImage ?? '';
        if (!img) console.warn(`[AS-APP] cardImage missing for "${e.shortName}" — not in API response or appSuite.cfg.json`);
        else if (!e.cardImage) console.warn(`[AS-APP] cardImage for "${e.shortName}" using JSON fallback: ${img}`);
        return img;
      })(),
      detailsPageURL: e.detailsPageURL,
      free: toBool(e.isFree),
      // Absent on older API responses — treat as enabled so downloads keep working.
      downloadEnabled: e.downloadEnabled != null ? toBool(e.downloadEnabled) : true,
      // Legacy single boolean kept for backward compat (appInfo is public API);
      // acceptsMeshTransfer is the per-tier source of truth for filtering.
      acceptsFileTransfer: legacyTransfer,
      acceptsMeshTransfer: meshTransfer,
      ...(e.allowSelfTransfer != null && { allowSelfTransfer: toBool(e.allowSelfTransfer) }),
      stickerTag: e.stickerTag ?? undefined,
      orderPrio: e.orderPrio,
      visibility: {
        staging: toBool(vStaging),
        production: toBool(vProd),
      },
      launchButtonBehaviour: {
        staging: bStaging,
        production: bProd,
      },
      ...(e.devOnly != null && { devOnly: toBool(e.devOnly) }),
      ...(e.hide != null && { hide: toBool(e.hide) }),
      ...(e.live != null && { live: toBool(e.live) }),
    };
  }
  return result;
}

let appSuiteConfigPromise: Promise<AppSuiteConfig> | null = null;

export function getAppSuiteConfig(): Promise<AppSuiteConfig> {
  if (!appSuiteConfigPromise) {
    appSuiteConfigPromise = (async () => {
      // localDev: skip all network calls — no files will exist on a developer's machine,
      // and a direct cross-origin apiFetch with credentials fails when the server returns
      // Access-Control-Allow-Origin: * (which rejects credentialed requests).
      if (localDev) {
        return { apps: defaultAppInfo as any, filterCategories: [] };
      }

      // Fetch supplemental JSON for filterCategories and cardImage fallbacks
      const jsonCfg = await fetchResource<AppSuiteConfig>('data/appSuite.cfg.json');

      // Try the API for a live app list
      try {
        const appListUrl = `${apiRoot}?function=appList`;
        devlog('[AS-APP] Fetching app list from API:', appListUrl);
        const res = await apiFetch(appListUrl);
        const apiData: { success: boolean; data: AppListEntry[]; filterCategories?: AppSuiteConfig['filterCategories'] } | null
          = res.ok ? await res.json() : null;
        if (apiData?.success && Array.isArray(apiData.data)) {
          devlog('[AS-APP] App list loaded from API:', apiData.data.length, 'apps');
          const apps = transformAppList(apiData.data, jsonCfg?.apps ?? {});
          return {
            apps,
            filterCategories: apiData.filterCategories ?? jsonCfg?.filterCategories ?? [],
          };
        }
        devlog('[AS-APP] API appList response not usable:', apiData);
      } catch (e) {
        devlog('[AS-APP] API appList failed:', e);
      }

      if (jsonCfg) {
        console.warn('[AS-APP] Using appSuite.cfg.json fallback — API appList unavailable.');
        return jsonCfg;
      }

      console.warn('[AS-APP] Using bundled defaultAppInfo fallback — both API and appSuite.cfg.json unavailable.');
      return { apps: defaultAppInfo as any, filterCategories: [] };
    })();
  }
  return appSuiteConfigPromise;
}

// ============================================================================
// App info cache (reads from suite config)
// ============================================================================

export let appInfoCached: boolean = false;
export let appInfo: any = {};
export { appInfo as appList };
export let filterCategories: AppSuiteConfig['filterCategories'] = [];

export const getAppInfo = async (): Promise<any> => {
  if (!appInfoCached) {
    try {
      const cfg = await getAppSuiteConfig();
      // Update the objects directly to ensure live bindings reflect the new data
      Object.keys(appInfo).forEach(key => delete appInfo[key]);
      Object.assign(appInfo, cfg.apps);
      
      filterCategories.length = 0;
      filterCategories.push(...cfg.filterCategories);

      appInfoCached = true;
      window.dispatchEvent(new CustomEvent('as-appinfo-ready', { detail: appInfo }));
    } catch (error) {
      console.error('[AS-APP] Failed to load appSuite.cfg.json:', error);
      appInfoCached = false;
      throw error;
    }
  }
  if (!isProduction) devlog('appInfo', appInfo);
  return appInfo;
};

/**
 * Resolve the deployment folder name for an app id, as reported by the appList
 * API (`folderName`). The storefront sign-in redirect lands on `/apps/{folderName}`,
 * which can differ from the app id when the two drift apart — always prefer the
 * folder name, falling back to the id only when the app isn't in the list.
 */
export const getAppFolderName = async (appId: string): Promise<string> => {
  const info = await getAppInfo();
  return info[appId]?.folderName ?? appId;
};

// ============================================================================
// App filtering helpers
// ============================================================================

/**
 * Whether an app accepts mesh transfers on the current deployment tier.
 * Resolves acceptsMeshTransfer[tier] ('local' uses the 'dev' value), falling
 * back to the legacy acceptsFileTransfer boolean. Also handles raw
 * (untransformed) entries — when the API is down getAppSuiteConfig returns the
 * cfg.json apps as-is, so acceptsMeshTransfer may be missing, a bare boolean,
 * or a partial object.
 */
export const appAcceptsMeshTransfer = (appConfig: any): boolean => {
  const tier = deploymentTier === 'local' ? 'dev' : deploymentTier;
  const nested = normalizeAcceptsMeshTransfer(appConfig?.acceptsMeshTransfer, appConfig?.id ?? 'unknown');
  return toBool(nested?.[tier] ?? appConfig?.acceptsFileTransfer);
};

const appPassesFilter = (
  key: string,
  appConfig: any,
  filter: 'all' | 'free' | 'paid',
  onlyTransferable: boolean,
  exclude: Array<string>
): boolean => {
  if (isProduction && appConfig?.visibility?.production === false) return false;
  if (!localDev && appConfig?.visibility?.staging === false) return false;
  if (onlyTransferable && !appAcceptsMeshTransfer(appConfig)) return false;
  if (exclude.includes(key)) return false;

  switch (filter) {
    case 'free': return appConfig?.free === true;
    case 'paid': return appConfig?.free !== true;
    case 'all':
    default:     return true;
  }
};

export const getFilteredApps = async (
  filter: 'all' | 'free' | 'paid' = 'all',
  onlyTransferable: boolean = true,
  exclude: Array<string> = []
): Promise<string[]> => {
  const appInfoData = await getAppInfo();

  return Object.entries(appInfoData)
    .filter(([key, value]) => appPassesFilter(key, value, filter, onlyTransferable, exclude))
    .map(([key]) => key);
};

export const getFilteredAppsSync = (
  filter: 'all' | 'free' | 'paid' = 'all',
  onlyTransferable: boolean = true,
  exclude: Array<string> = []
): string[] => {
  if (!appInfoCached) {
    console.error('[AS-APP] getFilteredAppsSync called before appInfo was loaded. This will result in an empty app list.');
    return [];
  }
  if (!isProduction) devlog(appInfo);
  return Object.entries(appInfo)
    .filter(([key, value]) => appPassesFilter(key, value, filter, onlyTransferable, exclude))
    .map(([key]) => key);
};

export const getAvailableAppKeys = (): Promise<string[]> => getFilteredApps('all', true, []);
export const getFreeApps = (): Promise<string[]> => getFilteredApps('free');
export const getPaidApps = (): Promise<string[]> => getFilteredApps('paid');

export const checkLicenseType = (): void => {
  //
};

// ============================================================================
// Per-app config
// ============================================================================

const appConfigCache = new Map<string, Promise<AppConfig>>();

// data/app.cfg.json is fetched relative to the current page so it resolves correctly
// whether the app runs standalone or under the app-suite root.
let rootAppConfigPromise: Promise<AppConfig | null> | null = null;
function getRootAppConfig(): Promise<AppConfig | null> {
  if (!rootAppConfigPromise) {
    // Fetch the page-relative app config in every tier, including local dev: a
    // standalone dev server (e.g. Vite) serves data/app.cfg.json next to the page,
    // so init({}) can resolve configuratorDetails without inlining credentials.
    rootAppConfigPromise = fetch('./data/app.cfg.json')
      .then(r => {
        if (r.ok) return r.json() as Promise<AppConfig>;
        console.warn('[app-config] No data/app.cfg.json found (HTTP ' + r.status + '); configuratorDetails must be passed to init() explicitly.');
        return null;
      })
      .catch((err) => {
        console.warn('[app-config] Could not load data/app.cfg.json:', err);
        return null;
      });
  }
  return rootAppConfigPromise;
}

export function getAppConfig(appId: string): Promise<AppConfig> {
  if (!appConfigCache.has(appId)) {
    appConfigCache.set(appId, (async () => {
      const [rootCfg, perApp] = await Promise.all([
        getRootAppConfig(),
        appId
          ? fetchResource<AppConfig>(`./data/app.cfg.json`).catch(() => null)
          : Promise.resolve(null)
      ]);
      return {
        hubspot: { ...(rootCfg?.hubspot ?? {}), ...(perApp?.hubspot ?? {}) },
        paramate: perApp?.paramate ?? rootCfg?.paramate,
        configuratorDetails: perApp?.configuratorDetails ?? rootCfg?.configuratorDetails,
      };
    })());
  }
  return appConfigCache.get(appId)!;
}

// Backward-compat shim — prefer getAppConfig(appId) for new callers
export function getAppSettings(appId?: string): Promise<AppSettings> {
  return getAppConfig(appId ?? '');
}
